import React, { useState } from "react";
import {
  FileText,
  Briefcase,
  CalendarDays,
  AlertCircle,
  History,
  Filter,
  Search,
} from "lucide-react";
import RightDrawer from "../../shared/components/RightDrawer";

const ICON_MAP = {
  contract: { Icon: FileText, bg: "bg-blue-50", color: "text-blue-600", badgeBg: "bg-blue-100 text-blue-700" },
  type: { Icon: Briefcase, bg: "bg-emerald-50", color: "text-emerald-600", badgeBg: "bg-emerald-100 text-emerald-700" },
  schedule: { Icon: CalendarDays, bg: "bg-purple-50", color: "text-purple-600", badgeBg: "bg-purple-100 text-purple-700" },
  alert: { Icon: AlertCircle, bg: "bg-orange-50", color: "text-orange-600", badgeBg: "bg-orange-100 text-orange-700" },
  history: { Icon: History, bg: "bg-red-50", color: "text-red-500", badgeBg: "bg-red-100 text-red-700" },
};

// Full activity history dataset for the RightDrawer
const ALL_ACTIVITIES = [
  {
    id: "act-1",
    icon: "contract",
    title: "New contract created",
    subtitle: "Rahul Sharma — Senior Software Engineer (CT-1001)",
    time: "10:30 AM",
    date: "Today, Aug 10, 2026",
    category: "Contracts",
    user: "Babulal Tandure",
    details: "Created full-time permanent contract CT-1001 with 90 days probation.",
  },
  {
    id: "act-2",
    icon: "type",
    title: "Employment type updated",
    subtitle: "Priya Nair — Full Time to Remote Full Time",
    time: "09:45 AM",
    date: "Today, Aug 10, 2026",
    category: "Updates",
    user: "Babulal Tandure",
    details: "Reclassified employment type from Full Time to Remote Full Time.",
  },
  {
    id: "act-3",
    icon: "schedule",
    title: "Work schedule assigned",
    subtitle: "Neha Gupta — General Shift (8.0 hrs/day)",
    time: "Yesterday, 04:15 PM",
    date: "Aug 9, 2026",
    category: "Schedules",
    user: "HR Admin",
    details: "Assigned Standard General Office Shift (9:00 AM - 6:00 PM).",
  },
  {
    id: "act-4",
    icon: "alert",
    title: "Contract expired",
    subtitle: "Amit Verma — Fixed-Term Contract (CT-1058)",
    time: "Yesterday, 02:00 PM",
    date: "Aug 9, 2026",
    category: "Contracts",
    user: "System Event",
    details: "Contract expired automatically after 12-month term completion.",
  },
  {
    id: "act-5",
    icon: "history",
    title: "History record added",
    subtitle: "Riya Malhotra — Probation Review Completed",
    time: "Aug 3, 2026",
    date: "Aug 3, 2026",
    category: "Updates",
    user: "Babulal Tandure",
    details: "Added performance review record and confirmed permanent status.",
  },
  {
    id: "act-6",
    icon: "contract",
    title: "Contract renewed",
    subtitle: "Kavya Reddy — Extended for 1 Year (CT-0988)",
    time: "Aug 1, 2026",
    date: "Aug 1, 2026",
    category: "Contracts",
    user: "Babulal Tandure",
    details: "Renewed fixed-term contract CT-0988 through Aug 2027.",
  },
  {
    id: "act-7",
    icon: "schedule",
    title: "Flexible Shift template updated",
    subtitle: "Core hours modified to 10:00 AM - 4:00 PM",
    time: "Jul 28, 2026",
    date: "Jul 28, 2026",
    category: "Schedules",
    user: "HR Admin",
    details: "Updated active template for 310 employees under Flexible Shift.",
  },
  {
    id: "act-8",
    icon: "type",
    title: "New employment classification created",
    subtitle: "External Consultant (CONSULTANT)",
    time: "Jul 22, 2026",
    date: "Jul 22, 2026",
    category: "Updates",
    user: "Babulal Tandure",
    details: "Added default notice period of 30 days and 0 days probation.",
  },
];

const RecentActivity = ({ data = [] }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = ["All", "Contracts", "Schedules", "Updates"];

  const filteredActivities = ALL_ACTIVITIES.filter((act) => {
    const matchesCat = activeCategory === "All" || act.category === activeCategory;
    const matchesSearch =
      act.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.subtitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.user.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <>
      {/* Widget Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col justify-between h-full min-h-[320px]">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Recent Activity</h3>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="text-xs font-medium text-[#147db5] hover:text-[#116d9d] transition-colors cursor-pointer"
          >
            View all
          </button>
        </div>

        {/* Activity List Preview */}
        {data.length ? (
          <div className="flex flex-col gap-0 divide-y divide-slate-50">
            {data.map((item) => {
              const iconConfig = ICON_MAP[item.icon] || ICON_MAP.contract;
              const { Icon, bg, color } = iconConfig;

              return (
                <div
                  key={item.id}
                  onClick={() => setDrawerOpen(true)}
                  className="flex items-center gap-3 py-3 hover:bg-slate-50/80 rounded-lg px-1 -mx-1 transition-colors cursor-pointer"
                >
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${bg}`}>
                    <Icon size={15} className={color} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 leading-tight">
                      {item.title}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                      {item.subtitle}
                    </p>
                  </div>
                  <span className="shrink-0 text-[10px] text-slate-400 font-medium">
                    {item.time}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-slate-400">No recent activity</p>
          </div>
        )}
      </div>

      {/* Right Drawer for View All */}
      <RightDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Recent Activity Log"
        description="Comprehensive audit trail of employment updates, contract changes, and work schedules."
        width="520px"
        footer={
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">
              Showing {filteredActivities.length} of {ALL_ACTIVITIES.length} events
            </span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        <div className="p-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by title, employee, or admin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-4 py-2 text-xs font-medium text-slate-700 outline-none focus:border-[#147db5] focus:bg-white transition-all"
            />
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            <Filter size={13} className="text-slate-400 mr-1 shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all shrink-0 ${activeCategory === cat
                    ? "bg-[#147db5] text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200/70"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Activity Detailed List */}
          <div className="space-y-3 pt-2">
            {filteredActivities.map((act) => {
              const iconConfig = ICON_MAP[act.icon] || ICON_MAP.contract;
              const { Icon, bg, color, badgeBg } = iconConfig;

              return (
                <div
                  key={act.id}
                  className="rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${bg}`}>
                      <Icon size={17} className={color} strokeWidth={2} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {act.title}
                        </p>
                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold ${badgeBg}`}>
                          {act.category}
                        </span>
                      </div>

                      <p className="mt-1 text-xs text-slate-600 font-medium">
                        {act.subtitle}
                      </p>

                      <p className="mt-1.5 text-xs text-slate-500 bg-slate-50 rounded-lg p-2 leading-relaxed">
                        {act.details}
                      </p>

                      <div className="mt-2.5 flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-100 pt-2">
                        <span>By <strong className="font-medium text-slate-600">{act.user}</strong></span>
                        <span>{act.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredActivities.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-xs text-slate-500 font-medium">No matching activity records found.</p>
                <p className="text-[11px] text-slate-400 mt-1">Try clearing your search or filter.</p>
              </div>
            )}
          </div>
        </div>
      </RightDrawer>
    </>
  );
};

export default RecentActivity;
